import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Form,
  Input,
  Label,
  Row,
  Col,
  Spinner,
  Card,
  CardBody,
  CardHeader,
} from "reactstrap";
import PopupModal from "../../components/Common/PopupModal";
import api from "../../api";

const PokemonToEconomy = () => {
  const [popup, setPopup] = useState({
    isOpen: false,
    message: "",
    backgroundColor: "#1d222e",
  });

  const [loading, setLoading] = useState(false);
  const [pokemonList, setPokemonList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("normal");
  const [selectedPokemon, setSelectedPokemon] = useState("");
  const [selectedGender, setSelectedGender] = useState("M");
  const [amount, setAmount] = useState(1);

  const categories = [
    { value: "normal", label: "Normal" },
    { value: "shiny", label: "Shiny" },
    { value: "shadow", label: "Shadow" },
    { value: "golden", label: "Golden" },
    { value: "diamond", label: "Diamond" },
    { value: "sapphire", label: "Sapphire" },
    { value: "ruby", label: "Ruby" },
    { value: "emerald", label: "Emerald" },
  ];

  const handlePopup = useCallback((message, backgroundColor = "#1d222e") => {
    setPopup({ isOpen: true, message, backgroundColor });

    setTimeout(() => {
      setPopup((prev) => ({ ...prev, isOpen: false }));
    }, 2000);
  }, []);

  const fetchPokemonList = useCallback(async (category) => {
    setLoading(true);
    try {
      const res = await api.get("/v1/moderator/available-pokemon", {
        params: { category },
        withCredentials: true,
      });
      setPokemonList(res.data.pokemon || []);
      setSelectedPokemon(""); // Reset selection when category changes
    } catch (err) {
      console.error("Failed to fetch Pokemon list:", err);
      handlePopup("Failed to fetch Pokemon list", "red");
    } finally {
      setLoading(false);
    }
  }, [handlePopup]);

  useEffect(() => {
    fetchPokemonList(selectedCategory);
  }, [selectedCategory, fetchPokemonList]);

  const handleAddPokemon = async () => {
    if (!selectedPokemon) {
      handlePopup("Please select a Pokemon", "red");
      return;
    }

    if (amount < 1) {
      handlePopup("Amount must be at least 1", "red");
      return;
    }

    try {
      const payload = {
        pokemon_name: selectedPokemon,
        gender: selectedGender,
        amount: parseInt(amount),
      };

      const res = await api.post("/v1/moderator/add-pokemon-to-economy", payload, {
        withCredentials: true,
      });

      handlePopup(res.data.message, "#1d222e");
      
      // Reset form
      setSelectedPokemon("");
      setAmount(1);
    } catch (err) {
      handlePopup(err.response?.data.detail || "Failed to add Pokemon to Economy", "red");
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <h4 className="mb-4">Add Pokemon to Economy</h4>

        <Card>
          <CardHeader>
            <h5 className="mb-0">Select Pokemon</h5>
          </CardHeader>
          <CardBody>
            <Form>
              <Row className="mb-3">
                <Col md="3">
                  <Label>Category</Label>
                  <Input
                    type="select"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col md="4">
                  <Label>Pokemon</Label>
                  {loading ? (
                    <div className="text-center">
                      <Spinner size="sm" />
                    </div>
                  ) : (
                    <Input
                      type="select"
                      value={selectedPokemon}
                      onChange={(e) => setSelectedPokemon(e.target.value)}
                      disabled={pokemonList.length === 0}
                    >
                      <option value="">-- Select Pokemon --</option>
                      {pokemonList.map((pokemon) => (
                        <option key={pokemon} value={pokemon}>
                          {pokemon}
                        </option>
                      ))}
                    </Input>
                  )}
                  {!loading && pokemonList.length === 0 && (
                    <small className="text-muted">No Pokemon found in this category</small>
                  )}
                </Col>

                <Col md="2">
                  <Label>Gender</Label>
                  <Input
                    type="select"
                    value={selectedGender}
                    onChange={(e) => setSelectedGender(e.target.value)}
                  >
                    <option value="M">Male</option>
                    <option value="F">Female</option>
                    <option value="U">Ungendered</option>
                  </Input>
                </Col>

                <Col md="2">
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    min="1"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </Col>

                <Col md="1" className="d-flex align-items-end">
                  <Button
                    color="primary"
                    className="w-100"
                    onClick={handleAddPokemon}
                    disabled={!selectedPokemon || loading}
                  >
                    Add
                  </Button>
                </Col>
              </Row>
            </Form>

            <div className="mt-3">
              <p className="text-muted mb-0">
                <strong>Note:</strong> Pokemon will be added directly to the Economy trainer account.
              </p>
            </div>
          </CardBody>
        </Card>
      </div>

      <PopupModal
        isOpen={popup.isOpen}
        message={popup.message}
        backgroundColor={popup.backgroundColor}
        onClose={() => setPopup({ ...popup, isOpen: false })}
      />
    </div>
  );
};

export default PokemonToEconomy;
